export type AnchorDraftClassification<TRow> =
  | { kind: 'unchanged' }
  | {
      kind: 'delta';
      newRows: TRow[];
      updatedRows: Array<TRow & { entityId: string }>;
      removedIds: string[];
    };

// '' / null / undefined are interchangeable in draft rows (empty optional
// cells vs fields the seed reconstruction cannot fill), so they are dropped
// before comparison; keys are sorted for a stable serialization. Exported so
// the autosave surface snapshots and the org-function submit's drop-unchanged
// check share the same notion of "unchanged".
export function serializeRowData(data: object): string {
  const entries = Object.entries(data as Record<string, unknown>)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

/**
 * Id-based diff of the captured table against the freshly derived seed rows
 * (ADR 0011, third amendment). Seeded rows carry the entity's `entityId`:
 * a draft matching its seed verbatim is untouched, a differing one becomes an
 * in-place update. Rows without an id — or with an id no seed has (stale
 * cache) — are appended additively. A seed id missing from the drafts becomes
 * a surgical removal (`removedIds`). Caller handles the empty-drafts
 * (untouched seeded table) case as pure navigation.
 */
export function classifyAnchorDrafts<
  TData extends object,
  TRow extends { data: TData; entityId?: string }
>(seedRows: readonly TRow[], draftRows: readonly TRow[]): AnchorDraftClassification<TRow> {
  const seedById = new Map<string, TRow>();
  for (const seed of seedRows) {
    if (seed.entityId) seedById.set(seed.entityId, seed);
  }
  const newRows: TRow[] = [];
  const updatedRows: Array<TRow & { entityId: string }> = [];
  const seenSeedIds = new Set<string>();
  for (const row of draftRows) {
    const seed = row.entityId === undefined ? undefined : seedById.get(row.entityId);
    if (row.entityId === undefined || seed === undefined) {
      newRows.push(row);
      continue;
    }
    seenSeedIds.add(row.entityId);
    if (serializeRowData(row.data) !== serializeRowData(seed.data)) {
      updatedRows.push({ ...row, entityId: row.entityId });
    }
  }
  const removedIds = [...seedById.keys()].filter((id) => !seenSeedIds.has(id));
  if (newRows.length === 0 && updatedRows.length === 0 && removedIds.length === 0) {
    return { kind: 'unchanged' };
  }
  return { kind: 'delta', newRows, updatedRows, removedIds };
}
